import { makeSearchInput } from 'components/common/SearchInput';
import { CommonTranslationKeys } from 'intl/keys';
import { EntityLabel } from 'components/common/SearchInput/types';
import { WithDataGridApi } from 'components/common/withDataGridApi';
import { withProcessesData } from 'components/common/withProcessesData';
import { ContractDataGrid } from 'components/contract/ContractDataGrid';
import { ContractDataGridActions } from 'components/contract/ContractDataGrid/ContractDataGridActions';
import { MemoTableAnchor } from 'components/tableAnchor';
import { useAppNavigation } from 'contexts/navigation';
import { usePermissions } from 'hooks/permissions/usePermissions';
import React, { ReactElement, useMemo } from 'react';
import { useProjectId } from 'store/project';
import { contractModelToContractOnView } from 'shared/domain/contract/mapping';
import { ContractOnView } from 'shared/domain/contract/types/view';
import { useCompanies } from 'store/company';
import { useContracts } from 'store/contract';
import { useInitialSearchEffect } from '../../../hooks/search/useInitialSearch';
import { useSearchUpdate } from '../../../hooks/search/useSearchUpdate';
const ContractSearchInput = makeSearchInput(EntityLabel.contract);

const header = {
  id: 'create-contract-button',
  textId: CommonTranslationKeys.buttonCreateContract,
  dataQaId: 'create-contract-button',
};

function Contracts(): ReactElement {
  const { items: contracts, loading, emptyListReason, setSearchPhrase } = useContracts();
  const { items: companies, loading: loadingCompanies } = useCompanies();
  const projectId = useProjectId();
  const { goToCreateContract } = useAppNavigation();
  const { canCreateContract } = usePermissions();

  useSearchUpdate(EntityLabel.contract, setSearchPhrase);
  useInitialSearchEffect(projectId, EntityLabel.contract, setSearchPhrase);

  const contractsOnView = useMemo(
    () =>
      contracts.map((contract) => {
        const contractOnView = contractModelToContractOnView(
          contract,
          companies
        );
        return {
          _id: contractOnView._id,
          label: contractOnView.label,
          parties: contractOnView.parties,
        } as ContractOnView;
      }),
    [contracts, companies]
  );

  const isLoading = loading || loadingCompanies;

  return (
    <WithDataGridApi>
      <MemoTableAnchor
        canCreate={canCreateContract}
        creationAction={goToCreateContract}
        TableComponent={() => (
          <ContractDataGrid
            data={contractsOnView}
            loading={isLoading}
            emptyListReason={isLoading ? undefined : emptyListReason}
          />
        )}
        loading={isLoading}
        headerWithCreation={header}
        SearchInput={ContractSearchInput}
        DataGridActions={ContractDataGridActions}
      />
    </WithDataGridApi>
  );
}

export const MemoContracts = React.memo(
  withProcessesData(Contracts)
);
